import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import samplesService, { SampleStatistics } from '../services/samplesService';

/**
 * Optimized hook for fetching and caching statistics
 * Implements:
 * - Request deduplication
 * - Smart caching with TTL
 * - Throttling to prevent excessive API calls
 * - Memoization of derived values
 */
export function useOptimizedStatistics(
  refreshInterval: number = 30000,
  options?: {
    excludeCompleted?: boolean;
    dateFrom?: Date;
    dateTo?: Date;
    activeSamplesOnly?: boolean;
  }
) {
  const [statistics, setStatistics] = useState<SampleStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cache management
  const lastFetchTime = useRef<number>(0);
  const fetchPromise = useRef<Promise<SampleStatistics> | null>(null);
  const cacheTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Constants
  const MIN_FETCH_INTERVAL = 5000; // Don't fetch more often than every 5 seconds
  const CACHE_TTL = 30000; // Cache for 30 seconds
  
  /**
   * Fetch statistics with deduplication and caching
   */
  const fetchStatistics = useCallback(async (force: boolean = false) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime.current;
    
    // If not forcing and we fetched recently, return cached data
    if (!force && timeSinceLastFetch < MIN_FETCH_INTERVAL && statistics) {
      return statistics;
    }
    
    // If there's already a fetch in progress, return that promise
    if (fetchPromise.current) {
      return fetchPromise.current;
    }
    
    // Create new fetch promise
    fetchPromise.current = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Add cache-busting parameter to force fresh data
        const stats = await samplesService.getStatistics(
          force ? Date.now().toString() : undefined,
          options
        );
        
        lastFetchTime.current = Date.now();
        setStatistics(stats);
        
        // Clear cache timeout and set new one
        if (cacheTimeout.current) {
          clearTimeout(cacheTimeout.current);
        }
        cacheTimeout.current = setTimeout(() => {
          // Mark cache as stale after TTL
          lastFetchTime.current = 0;
        }, CACHE_TTL);
        
        return stats;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des statistiques';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
        fetchPromise.current = null;
      }
    })();
    
    return fetchPromise.current;
  }, [statistics, options]);
  
  /**
   * Memoized derived values to prevent recalculation
   */
  const derivedMetrics = useMemo(() => {
    if (!statistics) return null;
    
    return {
      totalActive: statistics.total - (statistics.par_statut?.PRET || 0) - (statistics.par_statut?.REJETE || 0),
      completionRate: statistics.total > 0 
        ? ((statistics.par_statut?.PRET || 0) / statistics.total) * 100 
        : 0,
      urgentCount: statistics.par_priorite?.URGENTE || 0,
      hasOverdue: statistics.en_retard > 0,
      averageTurnaround: statistics.moyenne_duree_jours || 0,
    };
  }, [statistics]);
  
  /**
   * Manual refresh function (throttled)
   */
  const refresh = useCallback(() => {
    return fetchStatistics(true);
  }, [fetchStatistics]);
  
  /**
   * Set up auto-refresh interval
   */
  useEffect(() => {
    // Initial fetch
    fetchStatistics();
    
    // Set up interval for auto-refresh
    const interval = setInterval(() => {
      fetchStatistics();
    }, refreshInterval);
    
    // Cleanup
    return () => {
      clearInterval(interval);
      if (cacheTimeout.current) {
        clearTimeout(cacheTimeout.current);
      }
    };
  }, [fetchStatistics, refreshInterval]);
  
  return {
    statistics,
    derivedMetrics,
    loading,
    error,
    refresh,
    lastUpdated: lastFetchTime.current,
  };
}

/**
 * Hook for throttled value updates
 * Prevents excessive re-renders when values change rapidly
 */
export function useThrottledValue<T>(value: T, delay: number = 500): T {
  const [throttledValue, setThrottledValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setThrottledValue(value);
    }, delay);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);
  
  return throttledValue;
}